import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppThunk } from "@Store/index";
import axios from "axios";
import config from "@Config/index";
import smartSearchApi from "./smartFilterService";
import {
  SmartFilterState,
  SmartSearchRequest,
  SmartSearchResponseData,
  ExtractedAndApplied,
} from "./types";
import { COUNTRIES_WITH_MONTHLY_DURATION } from "constants/configConstants";
import { resolveBudgetRange } from "@Pages/Search/components/FilterSection/components/Filters/Budget/budgetBounds";
import { withStrictMonthCounts } from "@Pages/Search/utils/moveInMonthWindow";
import {
  applySearchGeoRadius,
  resolveRadiusExpandEnabled,
} from "@Pages/Search/utils/geoRadiusExpand";
import {
  ROOM_TYPE_FILTER,
  ROOM_TYPE_SPECIFIC,
  SHARING_OPTIONS_BATHROOM,
  SHARING_OPTIONS_KITCHEN,
} from "@Pages/Search/components/FilterSection/data/filtersList";

export const initialState: SmartFilterState = {
  query: "",
  isLoading: false,
  isCountLoading: false,
  isOpen: false,
  response: null,
  appliedFilters: null,
  extractedButNotApplied: null,
  inventoryCount: null,
  isIrrelevant: false,
  message: null,
  error: null,
};

const smartFilterSlice = createSlice({
  name: "smartFilterData",
  initialState,
  reducers: {
    setSmartFilterOpen(state, { payload }: PayloadAction<boolean>) {
      state.isOpen = payload;
    },
    setSmartFilterQuery(state, { payload }: PayloadAction<string>) {
      state.query = payload;
    },
    smartSearchRequesting(state) {
      state.isLoading = true;
      state.error = null;
      state.isIrrelevant = false;
      state.message = null;
    },
    smartSearchSuccess(state, { payload }: PayloadAction<SmartSearchResponseData>) {
      state.isLoading = false;
      state.response = payload;
      state.isIrrelevant = payload.is_irrelevant;
      state.message = payload.message;
      state.inventoryCount = payload.inventory_count;
      state.extractedButNotApplied = payload.extracted_but_not_applied;

      if (payload.is_irrelevant) {
        // Irrelevant query — clear any previously applied filters
        state.appliedFilters = null;
        state.inventoryCount = null;
      } else {
        state.appliedFilters = payload.extracted_and_applied;
      }
    },
    smartSearchFailure(state, { payload }: PayloadAction<string>) {
      state.isLoading = false;
      state.error = payload;
    },
    clearSmartFilters(state) {
      state.query = "";
      state.response = null;
      state.appliedFilters = null;
      state.extractedButNotApplied = null;
      state.inventoryCount = null;
      state.isIrrelevant = false;
      state.message = null;
      state.error = null;
    },
    clearSmartFilterResults(state) {
      state.response = null;
      state.appliedFilters = null;
      state.extractedButNotApplied = null;
      state.inventoryCount = null;
      state.isIrrelevant = false;
      state.message = null;
      state.error = null;
    },
    removeAppliedFilter(state, { payload }: PayloadAction<string>) {
      if (state.appliedFilters) {
        const updated = { ...state.appliedFilters };
        delete (updated as any)[payload];
        // If no filters left, clear everything
        if (Object.keys(updated).filter((k) => k !== "is_irrelevant").length === 0) {
          state.appliedFilters = null;
          state.inventoryCount = null;
        } else {
          state.appliedFilters = updated;
        }
      }
    },
    updateInventoryCount(state, { payload }: PayloadAction<number | null>) {
      state.inventoryCount = payload;
      state.isCountLoading = false;
    },
    setCountLoading(state, { payload }: PayloadAction<boolean>) {
      state.isCountLoading = payload;
    },
    removeExtractedButNotApplied(state, { payload }: PayloadAction<number>) {
      if (state.extractedButNotApplied) {
        state.extractedButNotApplied = state.extractedButNotApplied.filter(
          (_, index) => index !== payload,
        );
        if (state.extractedButNotApplied.length === 0) {
          state.extractedButNotApplied = null;
        }
      }
    },
    resetSmartFilterState() {
      return initialState;
    },
  },
});

export const {
  setSmartFilterOpen,
  setSmartFilterQuery,
  smartSearchRequesting,
  smartSearchSuccess,
  smartSearchFailure,
  clearSmartFilters,
  clearSmartFilterResults,
  removeAppliedFilter,
  updateInventoryCount,
  setCountLoading,
  removeExtractedButNotApplied,
  resetSmartFilterState,
} = smartFilterSlice.actions;

/**
 * Thunk to submit a smart search query.
 * Calls the NLP API and stores the response.
 * The caller is responsible for applying the extracted filters to the search page.
 */
export const submitSmartSearch =
  (request: SmartSearchRequest, signal?: AbortSignal): AppThunk =>
  async (dispatch) => {
    dispatch(smartSearchRequesting());
    try {
      const response = await smartSearchApi(request, signal);
      dispatch(smartSearchSuccess(response.data));
    } catch (error: any) {
      if (error?.name === "CanceledError" || error?.code === "ERR_CANCELED") {
        // Request was aborted, don't treat as error
        return;
      }
      const errorMessage =
        error?.response?.data?.message ||
        "Smart search is currently unavailable. Please try again later.";
      dispatch(smartSearchFailure(errorMessage));
    }
  };

const ALLOWED_SORT_KEYS = ["distance", "available_price", "created"];

/**
 * Converts the NLP API response `extracted_and_applied` object
 * into the internal filter state format used by the search page.
 *
 * The API response keys map directly to the existing filter URL params
 * used by the search page (sort_key, range_available_price, meta_types, etc.)
 * so we convert them to the internal `FilterState` format via `filtersMap`.
 */
export const mapNlpResponseToFilterState = (
  extractedFilters: ExtractedAndApplied,
  regionMinPrice?: number,
  regionMaxPrice?: number,
  isMonthlyCountry?: boolean,
  /** The region and its `price_stats`, so an open end resolves to the panel's own bounds. */
  region?: any,
  priceStats?: any,
): Record<string, string | string[]> => {
  const filterState: Record<string, string | string[]> = {};

  // Only apply sort_key if it's one of the allowed values
  if (extractedFilters.sort_key && ALLOWED_SORT_KEYS.includes(extractedFilters.sort_key)) {
    filterState.sortKey = extractedFilters.sort_order
      ? `${extractedFilters.sort_key},${extractedFilters.sort_order}`
      : `${extractedFilters.sort_key},desc`;
  }

  if (extractedFilters.range_available_price) {
    const [min, max] = extractedFilters.range_available_price.split(",");
    // NLP leaves an end open ("under £300" has no minimum); `priceStats` is what the Budget panel
    // draws its slider on, so filling and clamping from it keeps the pill, the panel and the
    // listing on one budget. Falls back to the region range when the count has not landed yet.
    filterState.budget = resolveBudgetRange([min, max], priceStats, region) ?? [
      Number(min) === 0 && regionMinPrice ? String(regionMinPrice) : min,
      !max && regionMaxPrice ? String(regionMaxPrice) : max,
    ];
  }

  if (extractedFilters.meta_types) {
    filterState.roomType = extractedFilters.meta_types.split(",");
  }

  if (extractedFilters.meta_unit_types) {
    filterState.accommodationType = extractedFilters.meta_unit_types.split(",");
  }

  if (extractedFilters.move_in_month) {
    filterState.moveInMonth = extractedFilters.move_in_month;
  }

  if (extractedFilters.meta_min_lease_duration) {
    const unit = isMonthlyCountry ? "months" : "weeks";
    filterState.minLeaseDuration = `${extractedFilters.meta_min_lease_duration} ${unit}`;
  }

  if (extractedFilters.meta_max_lease_duration) {
    const unit = isMonthlyCountry ? "months" : "weeks";
    filterState.maxLeaseDuration = `${extractedFilters.meta_max_lease_duration} ${unit}`;
  }

  if (extractedFilters.tags) {
    filterState.dualOccupancy = extractedFilters.tags.split(",");
  }

  if (extractedFilters.amenities) {
    filterState.amenities = extractedFilters.amenities.split(",");
  }

  if (extractedFilters.guarantor) {
    filterState.guarantor = extractedFilters.guarantor;
  }

  if (extractedFilters.available) {
    filterState.available = extractedFilters.available;
  }

  if (extractedFilters.region_radius) {
    filterState.region_radius = String(extractedFilters.region_radius);
  }

  // University and locality are handled via canonical_name navigation
  // rather than filter state — but we include them for pill display
  if (extractedFilters.university_canonical_name) {
    filterState.university = extractedFilters.university_canonical_name;
  } else if (extractedFilters.university) {
    filterState.university = extractedFilters.university;
  }

  if (extractedFilters.locality_canonical_name) {
    filterState.locality = extractedFilters.locality_canonical_name;
  } else if (extractedFilters.locality) {
    filterState.locality = extractedFilters.locality;
  }

  return filterState;
};

/**
 * Thunk to remove an individual filter pill and re-fetch inventory count.
 * Dispatches removeAppliedFilter, then calls inventory_filter_count API
 * with the remaining applied filters to update the count.
 * Follows the same param pattern as FilterSection.tsx's fetchCountData.
 */
export const removeFilterAndUpdateCount =
  (filterKey: string): AppThunk =>
  async (dispatch, getState) => {
    dispatch(removeAppliedFilter(filterKey));
    dispatch(setCountLoading(true));

    const state = getState();
    const appliedFilters = state.smartFilterData?.appliedFilters;
    const region = state.searchPageData?.region;
    const canonicalName = state.searchPageData?.canonical_name;

    // If no filters remain, clear count
    if (
      !appliedFilters ||
      Object.keys(appliedFilters).filter((k) => k !== "is_irrelevant").length === 0
    ) {
      dispatch(updateInventoryCount(null));
      return;
    }

    const countryShortName = region?.location?.country?.short_name || "";
    const isMonthlyCountry = COUNTRIES_WITH_MONTHLY_DURATION.includes(countryShortName);
    const currentFilters = state.searchPageData?.filters;

    // Build params matching the pattern from FilterSection.tsx
    const params: Record<string, any> = {
      status: "active",
      agg_keys: [
        "room_types",
        "bathroom_types",
        "kitchen_types",
        "lease_duration",
        "lease_duration_monthly",
        "meta_unit_types",
        "available",
        "price_stats",
        "flexible_stay_duration",
        "guarantor",
        "move_in_month",
        "flexible_move_in",
        "amenities_grouped",
        "bills_included",
      ].join(","),
      use_java_api: true,
      use_v2_structure: true,
      region_type: "circle",
      region_radius: appliedFilters.region_radius || (countryShortName === "AE" ? 60000 : 20000),
    };

    // Use 30000 region_radius for nearby requests
    if (typeof window !== "undefined") {
      const urlParams = new URLSearchParams(window.location.search);
      const localityParam = urlParams.get("locality");
      if (localityParam && localityParam.includes("near-by")) {
        params.region_radius = 30000;
      }
    }

    // Region scoping — use locality if present, otherwise region canonical name
    if (appliedFilters.locality_canonical_name) {
      params.locality_canonical_name = appliedFilters.locality_canonical_name;
    } else {
      params.region_canonical_name = canonicalName;
    }

    applySearchGeoRadius(
      params,
      currentFilters,
      region,
      state.searchPageData?.isRadiusExpanded,
      resolveRadiusExpandEnabled(state.searchPageData?.radiusExpandEnabled),
    );

    // Sort — use NLP sort only if allowed, otherwise fall back to page's current sort
    if (appliedFilters.sort_key && ALLOWED_SORT_KEYS.includes(appliedFilters.sort_key)) {
      params.sort_key = appliedFilters.sort_key;
      params.sort_order = appliedFilters.sort_order || "desc";
    } else if (currentFilters?.sortKey) {
      const [sortKey, sortOrder] = currentFilters.sortKey.split(",");
      params.sort_key = sortKey || "relevance_sorting_v2";
      params.sort_order = sortOrder || "desc";
    } else {
      params.sort_key = "relevance_sorting_v2";
      params.sort_order = "desc";
    }

    // Price — use weekly_range_price or range_available_price based on country
    if (appliedFilters.range_available_price) {
      // Resolved against the same price_stats the Budget panel is drawn on, so this count is for
      // the budget the panel will show once applied.
      const resolved = resolveBudgetRange(
        appliedFilters.range_available_price,
        state.searchPageData?.dynamicFilterCountData?.data?.data,
        region,
      );
      const priceRange = resolved ? resolved.join(",") : appliedFilters.range_available_price;

      if (isMonthlyCountry) {
        params.range_available_price = priceRange;
      } else {
        params.weekly_range_price = priceRange;
      }
      params.duration = isMonthlyCountry ? "monthly" : "weekly";
    }

    // meta_types — split into room_types, bathroom_types, kitchen_types (same as FilterSection)
    if (appliedFilters.meta_types) {
      const metaTypesArr = appliedFilters.meta_types.split(",");

      const roomTypeValues = [
        ...ROOM_TYPE_SPECIFIC.list.map((it: any) => it.value),
        ...ROOM_TYPE_FILTER.list.map((it: any) => it.value),
      ];
      const bathroomTypeValues = SHARING_OPTIONS_BATHROOM.list.map((it: any) => it.value);
      const kitchenTypeValues = SHARING_OPTIONS_KITCHEN.list.map((it: any) => it.value);

      const roomTypes = metaTypesArr.filter((v: string) => roomTypeValues.includes(v)).join(",");
      const bathroomTypes = metaTypesArr
        .filter((v: string) => bathroomTypeValues.includes(v))
        .join(",");
      const kitchenTypes = metaTypesArr
        .filter((v: string) => kitchenTypeValues.includes(v))
        .join(",");

      if (roomTypes) params.room_types = roomTypes;
      if (bathroomTypes) params.bathroom_types = bathroomTypes;
      if (kitchenTypes) params.kitchen_types = kitchenTypes;
    }
    if (appliedFilters.meta_unit_types) params.meta_unit_types = appliedFilters.meta_unit_types;
    if (appliedFilters.move_in_month) params.move_in_month = appliedFilters.move_in_month;
    if (appliedFilters.meta_min_lease_duration)
      params.meta_min_lease_duration = appliedFilters.meta_min_lease_duration;
    if (appliedFilters.meta_max_lease_duration)
      params.meta_max_lease_duration = appliedFilters.meta_max_lease_duration;
    if (appliedFilters.tags) params.tags = appliedFilters.tags;
    if (appliedFilters.amenities) params.amenities = appliedFilters.amenities;
    if (appliedFilters.guarantor) params.guarantor = appliedFilters.guarantor;
    if (appliedFilters.available) params.available = appliedFilters.available;

    try {
      const response = await axios.get(
        `${config.API_URL}/api/v1/inventories/inventory_filter_count`,
        // P1D-5529: same strict month as the search the count previews.
        { params: withStrictMonthCounts(params), ...config.apiParams },
      );
      const count = response?.data?.data?.meta?.count ?? null;
      dispatch(updateInventoryCount(count));
    } catch {
      // If count fetch fails, just clear the count
      dispatch(updateInventoryCount(null));
    }
  };

export default smartFilterSlice.reducer;
